import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AR Sky Map & New Moon Alerts - YISRAEL LEARNING CENTER
 * 
 * Real-time sky visualization and notification system:
 * - AR overlay showing sun/moon positions with camera feed
 * - New Moon alerts subscription
 * - Portal tracking visualization
 * - Jubilee Gate countdown
 * - Gyroscope-based AR tracking
 */

function ARSkyMap({ user }) {
  const [celestialData, setCelestialData] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("sky"); // sky, alerts, ar
  const [newMoonAlerts, setNewMoonAlerts] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [arMode, setArMode] = useState(false);
  const [arSupported, setArSupported] = useState(true);
  const [deviceOrientation, setDeviceOrientation] = useState({ alpha: 0, beta: 0, gamma: 0 });
  const [realtimePosition, setRealtimePosition] = useState({ sun: null, moon: null });
  const [calibrationOffset, setCalibrationOffset] = useState({ alpha: 0, beta: 0, gamma: 0 });
  const [isCalibrated, setIsCalibrated] = useState(false);
  
  // ─── Luminary-reference auto-calibration state (2026-04-24 update) ──────
  // When the student points their device at the Sun (or Moon if Sun is below
  // the horizon), we read the *actual* azimuth + altitude of that luminary
  // from the backend Live Observatory and derive a calibration offset that
  // makes the device's reading align with the live-sky truth. This gives us
  // a hard astronomical ground truth instead of a guessed baseline.
  const [calibrationReference, setCalibrationReference] = useState(null);
  //   { source: 'sun'|'moon', azimuth, altitude, takenAt: Date }
  const [autoCalibrating, setAutoCalibrating] = useState(false);
  const [calibrationError, setCalibrationError] = useState("");
  // 2026-06-01: Two-step calibration UX. When the user taps "Calibrate
  // to Sun/Moon" we enter `aimingMode` — a big crosshair + banner
  // appears, telling them to center the luminary in the frame and tap
  // again to lock. Removes the production failure where students tapped
  // Auto-Calibrate while pointed at the ground.
  const [aimingMode, setAimingMode] = useState(false);
  const [aimingTarget, setAimingTarget] = useState(null); // 'sun' | 'moon' | null
  
  const canvasRef = useRef(null);
  const arCanvasRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const previousUIState = useRef({ view: "sky", scrollPosition: 0 });

  // Real-time position calculation (imported from SkyMap logic)
  const calculateSunPosition = useCallback((date, lat, lon) => {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const hour = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
    
    let jd;
    if (month <= 2) {
      jd = Math.floor(365.25 * (year - 1 + 4716)) + Math.floor(30.6001 * (month + 13)) + day + hour / 24 - 1524.5;
    } else {
      jd = Math.floor(365.25 * (year + 4716)) + Math.floor(30.6001 * (month + 1)) + day + hour / 24 - 1524.5;
    }
    
    const n = jd - 2451545.0;
    const L = (280.460 + 0.9856474 * n) % 360;
    const g = ((357.528 + 0.9856003 * n) % 360) * Math.PI / 180;
    const lambda = L + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g);
    const epsilon = 23.439 - 0.0000004 * n;
    const epsilonRad = epsilon * Math.PI / 180;
    const declination = Math.asin(Math.sin(epsilonRad) * Math.sin(lambda * Math.PI / 180)) * 180 / Math.PI;
    const ra = Math.atan2(
      Math.cos(epsilonRad) * Math.sin(lambda * Math.PI / 180),
      Math.cos(lambda * Math.PI / 180)
    ) * 180 / Math.PI;
    
    const gmst = (280.46061837 + 360.98564736629 * (jd - 2451545.0)) % 360;
    const lst = (gmst + lon) % 360;
    const ha = (lst - ra) * Math.PI / 180;
    const latRad = lat * Math.PI / 180;
    const decRad = declination * Math.PI / 180;
    
    const altitude = Math.asin(
      Math.sin(latRad) * Math.sin(decRad) +
      Math.cos(latRad) * Math.cos(decRad) * Math.cos(ha)
    ) * 180 / Math.PI;
    
    let azimuth = Math.atan2(
      -Math.sin(ha),
      Math.tan(decRad) * Math.cos(latRad) - Math.sin(latRad) * Math.cos(ha)
    ) * 180 / Math.PI;
    azimuth = (azimuth + 360) % 360;
    
    return { altitude, azimuth, declination };
  }, []);

  const calculateMoonPosition = useCallback((date, lat, lon) => {
    // Simplified moon position calculation
    const synodic = 29.530588853;
    const refNewMoon = new Date(Date.UTC(2000, 0, 6, 18, 14, 0));
    const daysSince = (date.getTime() - refNewMoon.getTime()) / (1000 * 60 * 60 * 24);
    const ageDays = daysSince % synodic;
    const phase = ageDays / synodic;
    const illumination = 50 * (1 - Math.cos(2 * Math.PI * phase));
    
    // Approximate moon position (simplified)
    const moonLag = 12.2; // degrees per day
    const sunPos = calculateSunPosition(date, lat, lon);
    const moonOffset = (ageDays * moonLag) % 360;
    
    return {
      altitude: sunPos.altitude - 10 + Math.sin(phase * Math.PI * 2) * 20,
      azimuth: (sunPos.azimuth + moonOffset) % 360,
      illumination,
      phase,
      ageDays
    };
  }, [calculateSunPosition]);

  useEffect(() => {
    loadCelestialData();
    checkAlertSubscription();

    // Safety hatch: even if `loadCelestialData` never resolves (e.g.
    // a mobile Chrome geolocation timeout that never fires the reject
    // callback, or a hung axios call), the widget MUST exit the
    // "Loading AR Sky Map…" placeholder within 10 s so the student
    // isn't stuck watching a spinner.
    const loadingTimeout = setTimeout(() => setLoading(false), 10000);

    // Background refresh of sun/moon positions.
    // In AR mode the luminary moves ~15°/hour (0.25°/min). If we only refresh
    // every 5 min the overlay drifts up to 1.25° off — visible to the eye. So
    // when AR is active we poll every 60s (keeps within ~0.25°); when AR is
    // off we fall back to the cheaper 5-min cadence.
    const interval = setInterval(
      loadCelestialData,
      arMode ? 60000 : 300000,
    );
    return () => {
      clearInterval(interval);
      clearTimeout(loadingTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arMode]);

  useEffect(() => {
    if (canvasRef.current && !arMode) {
      drawSkyMap();
    }
  }, [celestialData, arMode]);

  // ═══════════════════════════════════════════════════════════════════════════
  // AR VIEWPORT FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  // Capture iOS-specific TRUE-north compass heading. The standard alpha is
  // MAGNETIC north on Android/Web, but iOS additionally exposes
  // `webkitCompassHeading` which IS already corrected to TRUE north — exactly
  // what astronomical azimuth uses. Prefer it whenever available; this alone
  // removes the magnetic-declination offset that was misaligning the sun icon.
  const compassHeadingRef = useRef(null);  // last seen webkitCompassHeading (true-north degrees CW)

  // Handle device orientation for gyroscope tracking.
  // Note: iOS 13+ permission request is also made up-front from inside the
  // user-gesture click handler in `startARViewport` — that's the one that
  // actually grants. This effect just attaches the listener once arMode flips
  // on (permission is already granted by then on iOS, or not required on
  // Android/desktop).
  useEffect(() => {
    const handleOrientation = (event) => {
      // iOS-only: `webkitCompassHeading` is the device heading in degrees
      // CLOCKWISE from TRUE north (already declination-corrected). When
      // available, this is more accurate than `event.alpha` (counter-clockwise
      // from MAGNETIC north on Android/Chromium).
      if (typeof event.webkitCompassHeading === "number" && !Number.isNaN(event.webkitCompassHeading)) {
        compassHeadingRef.current = event.webkitCompassHeading;
      }
      setDeviceOrientation({
        alpha: event.alpha || 0, // Compass direction (CCW from magnetic N on Android)
        beta: event.beta || 0,   // Front/back tilt
        gamma: event.gamma || 0  // Left/right tilt (device roll)
      });
    };

    if (arMode) {
      window.addEventListener('deviceorientation', handleOrientation);
      // Some Android browsers fire only `deviceorientationabsolute` reliably.
      window.addEventListener('deviceorientationabsolute', handleOrientation);
    }

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
      window.removeEventListener('deviceorientationabsolute', handleOrientation);
    };
  }, [arMode]);

  // Attach camera stream to the <video> element once it actually mounts.
  // FIXED 2026-04-25: previously the stream was set on `videoRef.current` from
  // inside `startARViewport` BEFORE `setArMode(true)` re-rendered the JSX, so
  // `videoRef.current` was always null and the video stayed black even though
  // camera permission had been granted.
  useEffect(() => {
    if (!arMode || !arSupported) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    try {
      if (video.srcObject !== stream) {
        video.srcObject = stream;
      }
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((e) => {
          // Autoplay can be blocked on some mobile browsers when the video
          // element comes in muted+playsInline; surface the warning but
          // don't unmount AR.
          console.warn("Video play() rejected:", e);
        });
      }
    } catch (e) {
      console.warn("Failed to attach AR camera stream:", e);
    }
  }, [arMode, arSupported]);

  // Update real-time positions.
  // FIXED 2026-04-25: previously used a broken client-side moon formula
  // (`sunAlt ± 20° · sin(phase)`) and hardcoded St. Louis coordinates, which
  // placed the sun/moon in the wrong sky for every user outside STL. Now
  // reads observer-local altitude+azimuth straight from `celestialData`
  // (backend `/api/celestial/live-observatory` with the user's GPS). If
  // celestialData isn't loaded yet, fall back to the legacy client-side math.
  //
  // FIXED 2026-02-16 (user-reported "icons blinking"):
  // `setRealtimePosition({ ... })` was creating a fresh object every
  // animation frame (~60 Hz), even when the celestial positions hadn't
  // materially changed — driving the draw effect to wipe + redraw the
  // canvas 60× a second. Combined with the every-60s `loadCelestialData`
  // refresh that resets `celestialData`, this produced the visible
  // flicker on the AR viewport. We now only commit a new
  // `realtimePosition` when sun/moon altitude or azimuth has drifted
  // beyond a small visual-noise threshold (0.02°), and we preserve
  // object identity when nothing changed so the draw effect doesn't
  // re-fire. Icons now hold steady.
  const lastCommittedPosRef = useRef({ sun: null, moon: null });
  const POS_EPSILON = 0.02; // degrees — below this, treat as "no change"
  useEffect(() => {
    if (!arMode) return;

    const significantlyMoved = (a, b) => {
      if (!a || !b) return true;
      return (
        Math.abs((a.altitude ?? 0) - (b.altitude ?? 0)) > POS_EPSILON ||
        Math.abs((a.azimuth ?? 0) - (b.azimuth ?? 0)) > POS_EPSILON
      );
    };

    const updatePositions = () => {
      const now = new Date();
      let sunPos, moonPos;

      if (celestialData?.sun && Number.isFinite(celestialData.sun.azimuth)) {
        sunPos = {
          altitude: celestialData.sun.altitude,
          azimuth: celestialData.sun.azimuth,
          declination: 0,
          hourAngle: 0,
        };
        moonPos = {
          altitude: celestialData.moon?.altitude ?? 0,
          azimuth: celestialData.moon?.azimuth ?? 0,
          phase: celestialData.moon?.illumination ?? 0,
          // Draw code reads `illumination` as a percent. celestialData stores
          // it as a 0-1 fraction (illumination_percent / 100), so multiply
          // back to 0-100 to avoid `undefined.toFixed()` crashes in the AR
          // overlay label.
          illumination: (celestialData.moon?.illumination ?? 0) * 100,
          age: celestialData.moon?.age ?? 0,
        };
      } else {
        // Fallback for first render before /live-observatory returns.
        const lat = celestialData?.observer?.latitude ?? 38.6270;
        const lon = celestialData?.observer?.longitude ?? -90.1994;
        sunPos = calculateSunPosition(now, lat, lon);
        moonPos = calculateMoonPosition(now, lat, lon);
      }

      setRealtimePosition((prev) => {
        const prevSun = lastCommittedPosRef.current.sun;
        const prevMoon = lastCommittedPosRef.current.moon;
        const sunChanged = significantlyMoved(prevSun, sunPos);
        const moonChanged = significantlyMoved(prevMoon, moonPos);
        if (!sunChanged && !moonChanged && prev?.sun && prev?.moon) {
          // Nothing visually changed — preserve object identity so the
          // canvas draw effect (deps: realtimePosition.sun) does NOT
          // re-fire. This is the core of the no-blink fix.
          return prev;
        }
        // Carry forward any sub-threshold value to keep small jitter
        // from being persistently re-committed.
        const next = {
          sun: sunChanged ? sunPos : prevSun || sunPos,
          moon: moonChanged ? moonPos : prevMoon || moonPos,
        };
        lastCommittedPosRef.current = next;
        return next;
      });

      if (arMode) {
        animationFrameRef.current = requestAnimationFrame(updatePositions);
      }
    };

    updatePositions();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [arMode, celestialData, calculateSunPosition, calculateMoonPosition]);

  // Draw AR overlay on canvas
  useEffect(() => {
    if (!arMode || !arCanvasRef.current || !realtimePosition.sun) return;

    const canvas = arCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Calculate screen positions based on CALIBRATED device orientation and celestial positions
    const calibrated = getCalibratedOrientation();
    const { alpha, beta, gamma } = calibrated;
    const sunPos = realtimePosition.sun;
    const moonPos = realtimePosition.moon;

    // ── Camera field-of-view (degrees). Most modern phone wide cameras are
    //    around 65-70° horizontal. Using the previous 90°/side meant we drew
    //    every luminary at ~half its real angular offset → "the sun icon is
    //    offset from the real sun" bug. We use 65° H and derive V from
    //    aspect ratio so circular objects stay round across rotation.
    const FOV_HORIZONTAL_DEG = 65;
    const FOV_VERTICAL_DEG = FOV_HORIZONTAL_DEG * (height / width);

    // Device-pointing direction in TRUE north-corrected degrees.
    //  - iOS:    `webkitCompassHeading` (already true-north, CW). Use directly.
    //  - Other:  fall back to calibrated `alpha`. The Auto-Calibrate flow
    //            absorbs the magnetic declination automatically because it
    //            offsets `alpha` against a luminary of known TRUE-north
    //            azimuth.
    const headingDeg = compassHeadingRef.current !== null
      ? (compassHeadingRef.current - calibrationOffset.alpha)
      : alpha;

    // Normalize an angular delta into [-180, 180] so azimuths near 0°/360°
    // don't produce huge wrong deltas (e.g. sun=350°, device=10° → real 20°).
    const normalizeDeg = (deg) => {
      let d = ((deg + 180) % 360 + 360) % 360 - 180;
      return d;
    };

    // Map azimuth/altitude → screen pixel coords with full attitude support.
    const mapToScreen = (azimuth, altitude) => {
      // Horizontal angular offset between luminary and where camera is pointed.
      const deltaAz = normalizeDeg(azimuth - headingDeg);
      // Vertical: device tilt-up is encoded as `beta`. When phone is held
      // upright facing forward, beta ≈ 90°. So the camera centerline points
      // at altitude (beta - 90).
      const cameraPitch = beta - 90;
      const deltaAlt = altitude - cameraPitch;

      // Project to NDC (-1..1) using FOV.
      const ndcX = deltaAz / (FOV_HORIZONTAL_DEG / 2);
      const ndcY = deltaAlt / (FOV_VERTICAL_DEG / 2);

      // Apply device ROLL (gamma) so the overlay rotates with the phone.
      const rollRad = (gamma || 0) * Math.PI / 180;
      const cosR = Math.cos(rollRad);
      const sinR = Math.sin(rollRad);
      const rotX = ndcX * cosR - ndcY * sinR;
      const rotY = ndcX * sinR + ndcY * cosR;

      // NDC → screen (center origin, Y inverted because screen Y grows down).
      const x = width / 2 + rotX * (width / 2);
      const y = height / 2 - rotY * (height / 2);

      // Mark off-screen positions so the caller can skip drawing rather than
      // clamp to the edge (clamping made the sun stick to the screen edge
      // even when the user wasn't actually pointing at it).
      const onScreen = x >= -20 && x <= width + 20 && y >= -20 && y <= height + 20;
      return { x, y, onScreen };
    };

    // Draw grid overlay
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      ctx.beginPath();
      ctx.moveTo(0, (height / 4) * i);
      ctx.lineTo(width, (height / 4) * i);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo((width / 4) * i, 0);
      ctx.lineTo((width / 4) * i, height);
      ctx.stroke();
    }

    // Draw horizon line (using calibrated beta)
    const horizonY = height / 2 - ((90 - (deviceOrientation.beta - calibrationOffset.beta)) / 90) * (height / 2);
    ctx.strokeStyle = 'rgba(135, 206, 235, 0.8)';
    ctx.lineWidth = 2;
    ctx.setLineDash([10, 5]);
    ctx.beginPath();
    ctx.moveTo(0, horizonY);
    ctx.lineTo(width, horizonY);
    ctx.stroke();
    ctx.setLineDash([]);

    // ── Center crosshair (calibration target) ─────────────────────────
    // The Auto-Calibrate flow assumes the user has centered the SUN or
    // MOON on the camera before tapping. Without a visible crosshair the
    // student tapped Auto-Calibrate while pointing at the road, which made
    // the sun/moon icons line up with NOTHING (production bug 2026-06-01).
    // Crosshair is a thin gold cross + small ring at canvas center.
    const cx = width / 2;
    const cy = height / 2;
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.85)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, 22, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - 32, cy); ctx.lineTo(cx - 10, cy);
    ctx.moveTo(cx + 10, cy); ctx.lineTo(cx + 32, cy);
    ctx.moveTo(cx, cy - 32); ctx.lineTo(cx, cy - 10);
    ctx.moveTo(cx, cy + 10); ctx.lineTo(cx, cy + 32);
    ctx.stroke();
    // Tiny dot dead-center
    ctx.fillStyle = '#FFD700';
    ctx.beginPath();
    ctx.arc(cx, cy, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Draw compass directions
    ctx.font = 'bold 16px Arial';
    ctx.fillStyle = '#FFD700';
    ctx.textAlign = 'center';
    const directions = ['N', 'E', 'S', 'W'];
    directions.forEach((dir, i) => {
      const dirAz = i * 90;
      const screenPos = mapToScreen(dirAz, 0);
      if (screenPos.x > 20 && screenPos.x < width - 20) {
        ctx.fillText(dir, screenPos.x, height - 20);
      }
    });

    // Draw Sun
    if (sunPos.altitude > -10) {
      const sunScreen = mapToScreen(sunPos.azimuth, sunPos.altitude);
      if (sunScreen.onScreen) {
        // Sun glow
        const gradient = ctx.createRadialGradient(sunScreen.x, sunScreen.y, 0, sunScreen.x, sunScreen.y, 40);
        gradient.addColorStop(0, 'rgba(255, 255, 0, 0.8)');
        gradient.addColorStop(0.5, 'rgba(255, 165, 0, 0.4)');
        gradient.addColorStop(1, 'rgba(255, 69, 0, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(sunScreen.x, sunScreen.y, 40, 0, Math.PI * 2);
        ctx.fill();

        // Sun core
        ctx.fillStyle = '#FFD700';
        ctx.beginPath();
        ctx.arc(sunScreen.x, sunScreen.y, 15, 0, Math.PI * 2);
        ctx.fill();

        // Sun label
        ctx.font = 'bold 12px Arial';
        ctx.fillStyle = '#FFD700';
        ctx.fillText(`☀️ Sun`, sunScreen.x, sunScreen.y + 35);
        ctx.font = '10px Arial';
        ctx.fillText(`Alt: ${sunPos.altitude.toFixed(1)}°`, sunScreen.x, sunScreen.y + 50);
      }
    }

    // Draw Moon
    if (moonPos && moonPos.altitude > -10) {
      const moonScreen = mapToScreen(moonPos.azimuth, moonPos.altitude);
      if (moonScreen.onScreen) {
        // Moon glow
        ctx.fillStyle = 'rgba(200, 200, 255, 0.3)';
        ctx.beginPath();
        ctx.arc(moonScreen.x, moonScreen.y, 25, 0, Math.PI * 2);
        ctx.fill();

        // Moon core
        ctx.fillStyle = '#C0C0C0';
        ctx.beginPath();
        ctx.arc(moonScreen.x, moonScreen.y, 12, 0, Math.PI * 2);
        ctx.fill();

        // Moon label
        ctx.font = 'bold 12px Arial';
        ctx.fillStyle = '#C0C0C0';
        ctx.fillText(`🌙 Moon`, moonScreen.x, moonScreen.y + 30);
        ctx.font = '10px Arial';
        ctx.fillText(`${(moonPos.illumination ?? 0).toFixed(0)}% illuminated`, moonScreen.x, moonScreen.y + 45);
      }
    }

    // Draw Portal/Gate indicator
    const portal = celestialData?.enochian?.current_gate ?? celestialData?.enochian?.portal ?? "?";
    ctx.font = 'bold 14px Arial';
    ctx.fillStyle = '#FFD700';
    ctx.textAlign = 'left';
    ctx.fillText(`Gate ${portal}`, 20, 30);
    ctx.font = '12px Arial';
    ctx.fillStyle = '#87CEEB';
    // Prefer the user-friendly lunar-month day (Day 16 of Ziv) over the
    // 1-364 solar-year counter. Falls back gracefully if either is missing.
    const lunarDay = celestialData?.enochian?.current_lunar_day;
    const solarDay = celestialData?.enochian?.day_of_year;
    const dayLabel = lunarDay != null ? `Day ${lunarDay}`
                   : solarDay != null ? `Solar Day ${solarDay}`
                   : 'Day ?';
    ctx.fillText(dayLabel, 20, 50);

  }, [arMode, realtimePosition, deviceOrientation, celestialData]);

  // Start AR viewport
  const startARViewport = async () => {
    // Save current UI state
    previousUIState.current = {
      view: view,
      scrollPosition: window.scrollY
    };

    try {
      // Check for camera access
      const constraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // iOS 13+ requires DeviceOrientationEvent.requestPermission() to be
      // invoked from inside the same user-gesture handler that started AR.
      // If we wait for the post-mount useEffect, Safari silently denies and
      // alpha/beta/gamma stay 0 → the overlay never tracks the device.
      // We pre-warm the permission here while still in the click handler.
      try {
        if (
          typeof DeviceOrientationEvent !== "undefined" &&
          typeof DeviceOrientationEvent.requestPermission === "function"
        ) {
          await DeviceOrientationEvent.requestPermission();
        }
      } catch (orientErr) {
        console.warn("Device orientation permission not granted:", orientErr);
        // Non-fatal — overlay still works in fixed-view mode.
      }

      // NOTE: videoRef.current is null here because the <video> element only
      // renders after `setArMode(true)` re-renders the component. Stash the
      // stream and let the post-mount effect attach it.
      setArMode(true);
      setArSupported(true);
    } catch (err) {
      console.warn("Camera access denied or unavailable:", err);
      // Surface a user-visible reason so the AR screen doesn't look "broken"
      // when it's simply that the user denied camera permission or HTTPS is
      // missing. The user can still calibrate manually in fallback mode.
      const reason =
        err?.name === "NotAllowedError"
          ? "Camera permission was denied. AR will run in fallback mode (no live camera). Tap 🌞 Auto-Calibrate to anchor against the sun."
          : err?.name === "NotFoundError"
          ? "No camera detected on this device. AR will run in fallback mode."
          : err?.message
          ? `Camera unavailable: ${err.message}. Running in fallback mode.`
          : "Camera unavailable. Running in fallback mode.";
      setCalibrationError(reason);
      // Fallback mode - AR overlay without camera
      setArMode(true);
      setArSupported(false);
    }
  };

  // Stop AR viewport and restore UI
  const stopARViewport = () => {
    // Stop camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Restore previous UI state without reloading
    setArMode(false);
    setView(previousUIState.current.view);
    
    // Restore scroll position
    setTimeout(() => {
      window.scrollTo(0, previousUIState.current.scrollPosition);
    }, 100);
  };

  // Toggle AR mode
  const toggleARMode = () => {
    if (arMode) {
      stopARViewport();
    } else {
      startARViewport();
    }
  };

  // Self-calibration: set current orientation as the zero-point baseline
  const calibrateAR = () => {
    setCalibrationOffset({
      alpha: deviceOrientation.alpha,
      beta: deviceOrientation.beta,
      gamma: deviceOrientation.gamma
    });
    setIsCalibrated(true);
    setCalibrationReference({ source: 'manual', azimuth: 0, altitude: 0, takenAt: new Date() });
    setCalibrationError("");
    // Auto-clear calibration indicator after 3 seconds
    setTimeout(() => setIsCalibrated(false), 3000);
  };

  // Two-step entry point: first tap arms aiming mode, second tap locks it in.
  const beginOrLockCalibration = async () => {
    if (!aimingMode) {
      // First tap → arm aiming mode (decide whether to align with sun or moon)
      setCalibrationError("");
      try {
        const coords = await new Promise((resolve) => {
          if (!navigator.geolocation) return resolve({ latitude: 31.7683, longitude: 35.2137 });
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
            () => resolve({ latitude: 31.7683, longitude: 35.2137 }),
            { timeout: 5000, maximumAge: 60000 }
          );
        });
        const token = localStorage.getItem("token");
        const backend = process.env.REACT_APP_BACKEND_URL;
        const resp = await fetch(
          `${backend}/api/celestial/live-observatory?latitude=${coords.latitude}&longitude=${coords.longitude}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!resp.ok) {
          throw new Error(
            resp.status === 401 || resp.status === 403
              ? "You need to sign in to use AR calibration."
              : `Observatory returned HTTP ${resp.status}`
          );
        }
        // Guard against Cloudflare / proxy HTML error pages that would
        // otherwise blow up `resp.json()` with `Unexpected token '<'`.
        const ct = (resp.headers.get("content-type") || "").toLowerCase();
        if (!ct.includes("application/json")) {
          throw new Error("Observatory is unreachable (bad gateway).");
        }
        const data = await resp.json();
        const obs = data?.live_observatory || data;
        const sunAlt = Number(obs?.solar_ephemeris?.altitude ?? -999);
        const moonAlt = Number(obs?.lunar_ephemeris?.altitude ?? -999);
        if (sunAlt > 5) setAimingTarget("sun");
        else if (moonAlt > 5) setAimingTarget("moon");
        else {
          setCalibrationError("Neither the Sun nor the Moon is above 5° right now — try when one is visible.");
          return;
        }
        setAimingMode(true);
      } catch (e) {
        setCalibrationError(e?.message || "Could not reach the Observatory");
      }
    } else {
      // Second tap → lock the calibration to the centered luminary
      await autoCalibrateFromLuminary();
      setAimingMode(false);
      setAimingTarget(null);
    }
  };

  // ─── Auto-calibration via live Sun / Moon ────────────────────────────────
  // Student aims their device at the Sun (or Moon). We fetch the luminary's
  // TRUE azimuth/altitude from the backend Observatory for their GPS, and
  // derive the calibration offset from the delta. This gives a hard
  // astronomical anchor instead of a guess.
  const autoCalibrateFromLuminary = async () => {
    setAutoCalibrating(true);
    setCalibrationError("");
    try {
      // Try to get GPS; fall back to Jerusalem
      const coords = await new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve({ latitude: 31.7683, longitude: 35.2137 });
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
          () => resolve({ latitude: 31.7683, longitude: 35.2137 }),
          { timeout: 5000, maximumAge: 60000 }
        );
      });
      
      const token = localStorage.getItem("token");
      const backend = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(
        `${backend}/api/celestial/live-observatory?latitude=${coords.latitude}&longitude=${coords.longitude}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!resp.ok) {
        throw new Error(
          resp.status === 401 || resp.status === 403
            ? "Please sign back in — your session expired."
            : `Observatory returned HTTP ${resp.status}`
        );
      }
      const ct = (resp.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) {
        throw new Error("Observatory unreachable (bad gateway).");
      }
      const data = await resp.json();
      const obs = data?.live_observatory || data;
      const sun = obs?.solar_ephemeris || obs?.sun || {};
      const moon = obs?.lunar_ephemeris || obs?.moon || {};
      
      // Prefer whichever luminary is above the horizon with the highest altitude.
      let ref = null;
      const sunAlt = Number(sun.altitude ?? sun.alt ?? -999);
      const moonAlt = Number(moon.altitude ?? moon.alt ?? -999);
      if (sunAlt > 5) {
        ref = { source: "sun", azimuth: Number(sun.azimuth ?? sun.az ?? 0), altitude: sunAlt };
      } else if (moonAlt > 5) {
        ref = { source: "moon", azimuth: Number(moon.azimuth ?? moon.az ?? 0), altitude: moonAlt };
      } else {
        throw new Error("Neither the Sun nor the Moon is high enough in the sky right now for a reliable anchor. Try again later (luminary must be ≥ 5° above horizon).");
      }
      
      // Derive calibration offset: rotate the device frame so the reading
      // at the moment of calibration matches the luminary's true-north
      // azimuth + actual altitude. On iOS we use webkitCompassHeading
      // (already TRUE-north corrected) when present, so the offset value
      // captures only mechanical mounting bias — magnetic declination is
      // already removed by the OS. On other platforms we fall back to
      // `alpha` (magnetic), and Auto-Calibrate's subtraction absorbs the
      // declination automatically.
      const liveHeading =
        compassHeadingRef.current !== null
          ? compassHeadingRef.current
          : deviceOrientation.alpha;
      const newOffset = {
        alpha: liveHeading - ref.azimuth,
        beta: deviceOrientation.beta - 90 - ref.altitude,
        gamma: deviceOrientation.gamma || 0,
      };
      setCalibrationOffset(newOffset);
      setCalibrationReference({ ...ref, takenAt: new Date() });
      setIsCalibrated(true);
      setTimeout(() => setIsCalibrated(false), 3500);
    } catch (err) {
      setCalibrationError(err?.message || "Auto-calibration failed");
    } finally {
      setAutoCalibrating(false);
    }
  };
  
  // ─── Auto-Recalibrate (drift correction, runs every 60s) ────────────────
  // Compass headings drift over time due to magnetic interference. After the
  // student has done a manual LOCK calibration, this silent routine
  // micro-corrects the offset every 60s — BUT only applies the new offset
  // if it's within 10° of the existing one. That way, if the student
  // happens to be pointed away from the Sun/Moon during a recal cycle,
  // we don't snap-rotate the entire sky map to wherever they happen to be
  // looking. Only gentle drift correction, never destructive override.
  const [autoRecalibrate, setAutoRecalibrate] = useState(true);
  const recalibrateIntervalRef = useRef(null);

  const silentRecalibrate = async () => {
    // Only run if a manual calibration has been established (we have a
    // calibrationReference). Otherwise we'd be calibrating against
    // arbitrary device orientation and producing garbage.
    if (!calibrationReference || calibrationReference.source === "manual") return;
    try {
      const coords = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve({ latitude: 31.7683, longitude: 35.2137 });
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
          () => resolve({ latitude: 31.7683, longitude: 35.2137 }),
          { timeout: 4000, maximumAge: 120000 }
        );
      });
      const token = localStorage.getItem("token");
      const backend = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(
        `${backend}/api/celestial/live-observatory?latitude=${coords.latitude}&longitude=${coords.longitude}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!resp.ok) return;
      // Silent recalibrate — bail if the response isn't JSON (Cloudflare
      // HTML error page during a brief network blip should NEVER throw
      // into the caller and blow up the sky map).
      const ct = (resp.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) return;
      const data = await resp.json();
      const obs = data?.live_observatory || data;
      const sun = obs?.solar_ephemeris || obs?.sun || {};
      const moon = obs?.lunar_ephemeris || obs?.moon || {};

      // Stick with the same luminary the user originally LOCKed against.
      // If that luminary is no longer above the horizon, abort silently.
      const useSun = calibrationReference.source === "sun";
      const target = useSun ? sun : moon;
      const targetAlt = Number(target.altitude ?? target.alt ?? -999);
      if (targetAlt < 5) return;
      const targetAz = Number(target.azimuth ?? target.az ?? 0);

      const liveHeading =
        compassHeadingRef.current !== null
          ? compassHeadingRef.current
          : deviceOrientation.alpha;
      const proposedAlpha = liveHeading - targetAz;

      // Drift-tolerance guard: only accept the new offset if it's within
      // ±10° of the existing one. Anything bigger means the phone isn't
      // pointed at the luminary right now — skip this cycle.
      let delta = proposedAlpha - calibrationOffset.alpha;
      // Normalize to [-180, 180]
      while (delta > 180) delta -= 360;
      while (delta < -180) delta += 360;
      if (Math.abs(delta) > 10) return;

      setCalibrationOffset((prev) => ({
        ...prev,
        alpha: proposedAlpha,
        beta: deviceOrientation.beta - 90 - targetAlt,
      }));
      setCalibrationReference((prev) => prev ? { ...prev, azimuth: targetAz, altitude: targetAlt, takenAt: new Date(), drift: true } : prev);
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    if (recalibrateIntervalRef.current) {
      clearInterval(recalibrateIntervalRef.current);
      recalibrateIntervalRef.current = null;
    }
    if (autoRecalibrate && arMode) {
      recalibrateIntervalRef.current = setInterval(silentRecalibrate, 60_000);
    }
    return () => {
      if (recalibrateIntervalRef.current) {
        clearInterval(recalibrateIntervalRef.current);
        recalibrateIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRecalibrate, arMode, calibrationReference?.source]);

  // ─── Accuracy safeguard: rate calibration freshness ──────────────────────
  // Returns {label, color} indicating how confident we are that the sky-map
  // overlays line up with reality. Degrades as time passes since the last
  // luminary anchor (devices drift).
  const getCalibrationAccuracy = () => {
    if (!calibrationReference) {
      return { label: "❌ Uncalibrated", color: "#ff6b6b", short: "uncalibrated" };
    }
    const ageSec = (Date.now() - new Date(calibrationReference.takenAt).getTime()) / 1000;
    if (calibrationReference.source === "manual") {
      return {
        label: ageSec < 120 ? "⚠ Manual (untethered)" : "⚠ Drifting — re-anchor",
        color: "#ff9f43",
        short: "manual",
      };
    }
    if (ageSec < 60) {
      return { label: `✓ Anchored on ${calibrationReference.source === "sun" ? "🌞 Sun" : "🌙 Moon"}`, color: "#5ec65e", short: "accurate" };
    }
    if (ageSec < 300) {
      return { label: `○ Drift possible (${Math.round(ageSec)}s old)`, color: "#ffd700", short: "drifting" };
    }
    return { label: "⚠ Stale — re-calibrate", color: "#ff9f43", short: "stale" };
  };

  // Get calibrated orientation values
  const getCalibratedOrientation = () => {
    return {
      alpha: deviceOrientation.alpha - calibrationOffset.alpha,
      beta: deviceOrientation.beta - calibrationOffset.beta,
      gamma: deviceOrientation.gamma - calibrationOffset.gamma
    };
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const loadCelestialData = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // GEOGRAPHIC ANCHOR: North American Continent - Mississippi River Valley (St. Louis/Cahokia)
      // Fallback coordinates: ~38.6270° N, 90.1994° W (St. Louis, MO - center of the Promised Land)
      let lat = 38.6270;
      let lon = -90.1994;
      
      // ALWAYS try to get user's device location first
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { 
              timeout: 10000,
              enableHighAccuracy: true,
              maximumAge: 300000 // Cache for 5 minutes
            });
          });
          lat = pos.coords.latitude;
          lon = pos.coords.longitude;
        } catch (e) {
          console.log("Geolocation denied/unavailable - using St. Louis/Cahokia default");
        }
      }
      
      // CRITICAL: backend expects `latitude`/`longitude` (NOT `lat`/`lon`).
      // The legacy short names silently dropped through to the Jerusalem
      // defaults (31.77, 35.21), which produced -30° sun altitude at US
      // afternoon — the inverted-sun bug reported 2026-06-03.
      const response = await axios.get(
        `${API}/celestial/live-observatory?latitude=${lat}&longitude=${lon}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Also fetch the centralized lunar day from /api/celestial/data so the AR
      // overlay can show the friendly "Day 16 of Ziv" lunar-month number in
      // addition to the 364-day solar-year counter. Failure here is non-fatal.
      let lunarDay = null;
      let lunarMonthName = null;
      try {
        const dataResp = await axios.get(
          `${API}/celestial/data?latitude=${lat}&longitude=${lon}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        lunarDay = dataResp.data?.enochian?.current_lunar_day ?? null;
        lunarMonthName = dataResp.data?.biblical?.date?.month_name ?? null;
      } catch (e) { /* keep going with live-observatory only */ }

      // Extract nested data structure
      const liveObs = response.data?.live_observatory || {};
      const solar = liveObs.solar_ephemeris || {};
      const lunar = liveObs.lunar_ephemeris || {};
      const enochian = response.data?.enochian_calendar || liveObs.enochian_order || {};
      const jubilee = liveObs.jubilee_91_governor || {};

      // Normalize to component expected format
      // FIXED 2026-04-25: now uses backend-computed altitude/azimuth (true
      // observer-local coordinates) instead of hardcoded defaults that caused
      // the sun/moon to appear in the wrong Sky Map position.
      // FIXED 2026-06-01: jubilee.day_of_year was a typo — backend returns
      // `current_day_of_year`. The wrong field name was always undefined,
      // which is why the AR overlay displayed "Day: ?" on production.
      const normalizedData = {
        sun: {
          longitude: solar.ecliptic_longitude || 0,
          altitude: Number.isFinite(solar.altitude) ? solar.altitude : 0,
          azimuth: Number.isFinite(solar.azimuth) ? solar.azimuth : 0,
          current_gate: solar.current_gate
        },
        moon: {
          longitude: lunar.ecliptic_longitude || 0,
          altitude: Number.isFinite(lunar.altitude) ? lunar.altitude : 0,
          azimuth: Number.isFinite(lunar.azimuth) ? lunar.azimuth : 0,
          phase_name: lunar.phase || "Unknown",
          illumination: (lunar.illumination_percent || 0) / 100,
          age: lunar.days_since_new || 0
        },
        observer: { latitude: lat, longitude: lon },
        enochian: {
          portal: solar.current_gate || 1,
          current_gate: solar.current_gate || null,
          day_name: enochian.day_name || "Unknown",
          day_of_year: jubilee.current_day_of_year || jubilee.day_of_year || null,
          current_lunar_day: lunarDay,
          lunar_month_name: lunarMonthName,
          jubilee_gate: jubilee.is_jubilee_gate_day || false,
          quarter: jubilee.current_quarter || 1
        }
      };
      
      setCelestialData(normalizedData);
      setLastSyncAt(new Date());
      
      // Check for New Moon alert
      const moonPhase = response.data?.moon?.phase_name?.toLowerCase() || "";
      if (moonPhase.includes("new moon") && newMoonAlerts) {
        setAlertMessage("🌑 NEW MOON ALERT: The moon has entered a new cycle!");
      }
      
    } catch (err) {
      console.error("Error loading celestial data:", err);
    } finally {
      setLoading(false);
    }
  };

  const checkAlertSubscription = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMoonAlerts(response.data?.new_moon_alerts || false);
    } catch (err) {
      console.error("Error checking alert subscription:", err);
    }
  };

  const toggleNewMoonAlerts = async () => {
    try {
      const token = localStorage.getItem("token");
      const endpoint = newMoonAlerts ? "/progress/new-moon-alert/unsubscribe" : "/progress/new-moon-alert/subscribe";
      
      await axios.post(`${API}${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewMoonAlerts(!newMoonAlerts);
      setAlertMessage(newMoonAlerts ? "Unsubscribed from New Moon alerts" : "Subscribed to New Moon alerts!");
      setTimeout(() => setAlertMessage(""), 3000);
    } catch (err) {
      console.error("Error toggling alerts:", err);
    }
  };

  const drawSkyMap = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - 40;

    // Clear canvas
    ctx.fillStyle = "#0a0a1a";
    ctx.fillRect(0, 0, width, height);

    // If we don't have celestial data yet, draw a placeholder circle
    // + "Loading live sky" caption so the dial never looks broken.
    if (!celestialData) {
      ctx.strokeStyle = "#333";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = "#87CEEB";
      ctx.font = "bold 14px Georgia, serif";
      ctx.textAlign = "center";
      ctx.fillText("Loading live sky…", centerX, centerY);
      ctx.font = "11px Georgia, serif";
      ctx.fillStyle = "#666";
      ctx.fillText("connecting to observatory", centerX, centerY + 20);
      return;
    }
    
    // Draw sky gradient
    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    gradient.addColorStop(0, "#1a1a3a");
    gradient.addColorStop(1, "#0a0a1a");
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw horizon circle
    ctx.strokeStyle = "#333";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();
    
    // Draw cardinal directions
    ctx.fillStyle = "#666";
    ctx.font = "14px Arial";
    ctx.textAlign = "center";
    ctx.fillText("N", centerX, centerY - radius - 10);
    ctx.fillText("S", centerX, centerY + radius + 20);
    ctx.fillText("E", centerX + radius + 15, centerY + 5);
    ctx.fillText("W", centerX - radius - 15, centerY + 5);
    
    // Draw 12 Portal lines
    ctx.strokeStyle = "#333";
    ctx.lineWidth = 1;
    for (let i = 0; i < 12; i++) {
      const angle = (i * 30 - 90) * Math.PI / 180;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + radius * Math.cos(angle),
        centerY + radius * Math.sin(angle)
      );
      ctx.stroke();
      
      // Portal numbers
      ctx.fillStyle = "#444";
      ctx.font = "10px Arial";
      ctx.fillText(
        `G${i + 1}`,
        centerX + (radius - 25) * Math.cos(angle),
        centerY + (radius - 25) * Math.sin(angle) + 3
      );
    }
    
    // Draw Sun — use OBSERVER-LOCAL azimuth + altitude (NOT ecliptic
    // longitude). Ecliptic longitude is the sun's place on the zodiac and
    // has nothing to do with where it appears in the sky from the
    // observer's standpoint. The 2026-06-03 production bug — sun shown
    // in the upper-right at altitude -30° — was caused by reading
    // `longitude` for the dial angle. Below-horizon objects are not
    // drawn (they're not in the visible sky).
    if (celestialData.sun && Number.isFinite(celestialData.sun.altitude)) {
      const sunAz = Number(celestialData.sun.azimuth) || 0;
      const sunAlt = Number(celestialData.sun.altitude) || 0;

      if (sunAlt > 0) {
        // Map: azimuth=0 (N) → top (canvas angle -π/2). r = 0 at zenith
        // (alt=90), r = full at horizon (alt=0).
        const sunAngle = (sunAz - 90) * Math.PI / 180;
        const sunRadius = radius * Math.max(0, Math.min(1, 1 - sunAlt / 90));

        const sunX = centerX + sunRadius * Math.cos(sunAngle);
        const sunY = centerY + sunRadius * Math.sin(sunAngle);

        // Sun glow
        const sunGradient = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 30);
        sunGradient.addColorStop(0, "#FFD700");
        sunGradient.addColorStop(0.5, "rgba(255, 215, 0, 0.3)");
        sunGradient.addColorStop(1, "transparent");
        ctx.fillStyle = sunGradient;
        ctx.beginPath();
        ctx.arc(sunX, sunY, 30, 0, Math.PI * 2);
        ctx.fill();

        // Sun core
        ctx.fillStyle = "#FFD700";
        ctx.beginPath();
        ctx.arc(sunX, sunY, 12, 0, Math.PI * 2);
        ctx.fill();

        // Sun label
        ctx.fillStyle = "#FFD700";
        ctx.font = "12px Arial";
        ctx.fillText("☀️", sunX - 8, sunY - 20);
      } else {
        // 2026-06-05: per user spec, no below-horizon graphic at all.
        // The Sun must not render anywhere on the radar when it's
        // beneath the local horizon.
      }
    }
    
    // Draw Moon — same azimuth/altitude treatment as the Sun above.
    if (celestialData.moon && Number.isFinite(celestialData.moon.altitude)) {
      const moonAz = Number(celestialData.moon.azimuth) || 0;
      const moonAlt = Number(celestialData.moon.altitude) || 0;

      if (moonAlt > 0) {
        const moonAngle = (moonAz - 90) * Math.PI / 180;
        const moonRadius = radius * Math.max(0, Math.min(1, 1 - moonAlt / 90));

        const moonX = centerX + moonRadius * Math.cos(moonAngle);
        const moonY = centerY + moonRadius * Math.sin(moonAngle);

        // Moon glow
        const moonGradient = ctx.createRadialGradient(moonX, moonY, 0, moonX, moonY, 20);
        moonGradient.addColorStop(0, "#C0C0C0");
        moonGradient.addColorStop(0.5, "rgba(192, 192, 192, 0.3)");
        moonGradient.addColorStop(1, "transparent");
        ctx.fillStyle = moonGradient;
        ctx.beginPath();
        ctx.arc(moonX, moonY, 20, 0, Math.PI * 2);
        ctx.fill();

        // Moon core
        ctx.fillStyle = "#C0C0C0";
        ctx.beginPath();
        ctx.arc(moonX, moonY, 8, 0, Math.PI * 2);
        ctx.fill();

        // Moon label
        ctx.fillStyle = "#C0C0C0";
        ctx.font = "12px Arial";
        ctx.fillText("🌙", moonX - 8, moonY - 15);
      } else {
        // 2026-06-05: per user spec, ABSOLUTELY no below-horizon
        // graphic inside or adjacent to the Gate segments. Previously
        // we drew a "🌙 below" text marker just outside the dial; the
        // user wants the radar to render NOTHING for sub-horizon
        // objects ("only displays icons for celestial objects whose
        // current altitude coordinates are greater than 0°").
      }
    }
    
    // 2026-02-21 fix: if we have celestialData but BOTH luminaries are
    // below the horizon (typical at civil night when the app is used
    // to plan lessons), the sky dial can look eerily empty. Draw a
    // clear on-canvas indicator so students understand the sky is
    // being tracked — the sun/moon just aren't visible right now.
    const sunAltNum = Number(celestialData?.sun?.altitude);
    const moonAltNum = Number(celestialData?.moon?.altitude);
    const sunBelow = Number.isFinite(sunAltNum) && sunAltNum <= 0;
    const moonBelow = Number.isFinite(moonAltNum) && moonAltNum <= 0;
    if (sunBelow && moonBelow) {
      ctx.fillStyle = "#87CEEB";
      ctx.font = "bold 14px Georgia, serif";
      ctx.textAlign = "center";
      ctx.fillText("Both luminaries below horizon", centerX, centerY - 10);
      ctx.font = "12px Georgia, serif";
      ctx.fillStyle = "#aaa";
      ctx.fillText(
        `☀ ${sunAltNum.toFixed(1)}°   🌙 ${moonAltNum.toFixed(1)}°`,
        centerX,
        centerY + 14
      );
      ctx.fillText("Live tracking active", centerX, centerY + 34);
    } else if (sunBelow && !moonBelow) {
      // Show a small subtitle to explain the sun's absence — moon is
      // rendered by the block above.
      ctx.fillStyle = "#888";
      ctx.font = "11px Georgia, serif";
      ctx.textAlign = "center";
      ctx.fillText(
        `☀ below horizon (${sunAltNum.toFixed(1)}°)`,
        centerX,
        centerY + radius - 6
      );
    }

    // Draw current portal highlight
    if (celestialData.enochian?.portal) {
      const portal = celestialData.enochian.portal;
      const portalAngle1 = ((portal - 1) * 30 - 90) * Math.PI / 180;
      const portalAngle2 = (portal * 30 - 90) * Math.PI / 180;
      
      ctx.fillStyle = "rgba(255, 215, 0, 0.15)";
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, portalAngle1, portalAngle2);
      ctx.closePath();
      ctx.fill();
      
      ctx.strokeStyle = "#FFD700";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, portalAngle1, portalAngle2);
      ctx.stroke();
    }
  };

  if (loading) {
    return (
      <div className="card" data-testid="ar-sky-map-loading" style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ fontSize: "2rem", marginBottom: "15px" }}>🌌</div>
        <p style={{ color: "#888", marginBottom: 14 }}>Loading AR Sky Map...</p>
        <button
          data-testid="ar-sky-map-loading-retry"
          onClick={() => { setLoading(false); loadCelestialData(); }}
          style={{
            padding: "8px 18px",
            borderRadius: 8,
            background: "transparent",
            color: "#FFD700",
            border: "1px solid #FFD70088",
            cursor: "pointer",
            fontSize: "0.85rem",
          }}
        >Tap here if this is taking too long</button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AR VIEWPORT FULL-SCREEN OVERLAY
  // ═══════════════════════════════════════════════════════════════════════════
  if (arMode) {
    return (
      <div 
        data-testid="ar-viewport"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          background: "#000"
        }}
      >
        {/* Camera Video Feed (or fallback background) */}
        {arSupported ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover"
            }}
          />
        ) : (
          /* Fallback: Sky Map grid at 50% transparency behind overlay */
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "linear-gradient(to bottom, #0a0a2a 0%, #1a1a3a 50%, #0a0a1a 100%)",
            opacity: 0.5
          }}>
            {/* Grid pattern */}
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `
                linear-gradient(rgba(255,215,0,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,215,0,0.1) 1px, transparent 1px)
              `,
              backgroundSize: "50px 50px"
            }} />
          </div>
        )}

        {/* AR Canvas Overlay */}
        <canvas
          ref={arCanvasRef}
          width={window.innerWidth}
          height={window.innerHeight}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none"
          }}
        />

        {/* AR Info Panel */}
        <div style={{
          position: "absolute",
          top: 20,
          right: 20,
          background: "rgba(0,0,0,0.7)",
          padding: "15px",
          borderRadius: "12px",
          border: "1px solid rgba(255,215,0,0.3)",
          minWidth: "180px"
        }}>
          <div style={{ color: "#FFD700", fontWeight: "bold", marginBottom: "10px" }}>
            📡 AR Live View
          </div>
          <div style={{ color: "#87CEEB", fontSize: "0.9rem" }}>
            <div data-testid="ar-gate-label">
              Gate: <strong>{celestialData?.enochian?.current_gate ?? celestialData?.enochian?.portal ?? "?"}</strong>
              <span style={{ color: "#888", fontSize: "0.75rem" }}> / 6</span>
            </div>
            {celestialData?.enochian?.current_lunar_day != null && (
              <div data-testid="ar-lunar-day-label">
                Day: <strong>{celestialData.enochian.current_lunar_day}</strong>
                {celestialData?.enochian?.lunar_month_name && (
                  <span style={{ color: "#aaa", fontSize: "0.75rem" }}>
                    {" "}of {celestialData.enochian.lunar_month_name}
                  </span>
                )}
              </div>
            )}
            {celestialData?.enochian?.day_of_year && (
              <div data-testid="ar-solar-day-label" style={{ color: "#888", fontSize: "0.78rem" }}>
                Solar Day: {celestialData.enochian.day_of_year} / 364
              </div>
            )}
          </div>
          {!arSupported && (
            <div style={{ color: "#FFA500", fontSize: "0.8rem", marginTop: "10px" }}>
              ⚠️ Camera unavailable - Fallback mode
            </div>
          )}
          <div style={{ color: "#666", fontSize: "0.7rem", marginTop: "10px" }}>
            Point device at sky
          </div>
        </div>

        {/* Gyroscope Data + Calibration Status */}
        <div style={{
          position: "absolute",
          bottom: 100,
          left: 20,
          background: "rgba(0,0,0,0.5)",
          padding: "10px",
          borderRadius: "8px",
          fontSize: "0.75rem",
          color: "#666"
        }}>
          <div>α: {deviceOrientation.alpha?.toFixed(1)}°</div>
          <div>β: {deviceOrientation.beta?.toFixed(1)}°</div>
          <div>γ: {deviceOrientation.gamma?.toFixed(1)}°</div>
          {(calibrationOffset.alpha !== 0 || calibrationOffset.beta !== 0) && (
            <div style={{ color: "#90EE90", marginTop: "4px", fontSize: "0.65rem" }}>
              Calibrated
            </div>
          )}
        </div>

        {/* Calibrate Buttons — manual + live-luminary auto */}
        <div style={{
          position: "absolute",
          bottom: 30,
          left: 20,
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          maxWidth: "70%"
        }}>
          <button
            onClick={calibrateAR}
            data-testid="ar-calibrate-btn"
            style={{
              background: isCalibrated ? "rgba(144,238,144,0.8)" : "rgba(255,215,0,0.8)",
              color: "#000",
              border: "none",
              padding: "10px 18px",
              borderRadius: "20px",
              fontSize: "0.82rem",
              fontWeight: "bold",
              cursor: "pointer",
              boxShadow: "0 2px 12px rgba(0,0,0,0.3)"
            }}
          >
            {isCalibrated ? "✓ Calibrated" : "⌖ Calibrate"}
          </button>
          <button
            onClick={beginOrLockCalibration}
            disabled={autoCalibrating}
            data-testid="ar-auto-calibrate-btn"
            title={
              aimingMode
                ? `Center the ${aimingTarget === "moon" ? "Moon" : "Sun"} on the crosshair, then tap LOCK`
                : "Two-step calibration: tap once → aim crosshair at the Sun/Moon → tap LOCK"
            }
            style={{
              background: autoCalibrating
                ? "rgba(100,100,100,0.7)"
                : aimingMode
                ? "linear-gradient(135deg, #FF6347 0%, #FFA500 100%)"
                : "linear-gradient(135deg, rgba(255,165,0,0.95) 0%, rgba(255,215,0,0.95) 100%)",
              color: "#000",
              border: "none",
              padding: "10px 16px",
              borderRadius: "20px",
              fontSize: "0.82rem",
              fontWeight: "bold",
              cursor: autoCalibrating ? "wait" : "pointer",
              boxShadow: aimingMode ? "0 0 14px rgba(255,99,71,0.6)" : "0 2px 12px rgba(0,0,0,0.3)",
              animation: aimingMode ? "ar-pulse 1.4s ease-in-out infinite" : "none",
            }}
          >
            {autoCalibrating
              ? "Anchoring…"
              : aimingMode
              ? `🔒 LOCK on ${aimingTarget === "moon" ? "MOON" : "SUN"}`
              : "🌞 Calibrate to Sun/Moon"}
          </button>
          {/* Accuracy safeguard chip */}
          {(() => {
            const acc = getCalibrationAccuracy();
            return (
              <span
                data-testid="ar-accuracy-chip"
                data-accuracy={acc.short}
                style={{
                  background: "rgba(0,0,0,0.65)",
                  color: acc.color,
                  border: `1px solid ${acc.color}`,
                  padding: "5px 10px",
                  borderRadius: 12,
                  fontSize: "0.72rem",
                  fontWeight: "bold",
                  whiteSpace: "nowrap"
                }}
              >
                {acc.label}
              </span>
            );
          })()}
          {/* Auto-tracking badge — confirms background luminary refresh is live.
              Sun & moon move ~15°/hr (0.25°/min). In AR mode we re-fetch
              observer-local alt/az every 60s so the overlay walks across the
              sky with the real luminary (≤ 0.25° drift between syncs). */}
          {lastSyncAt && (() => {
            const ageSec = Math.round((Date.now() - lastSyncAt.getTime()) / 1000);
            const stale = ageSec > 90;
            return (
              <span
                data-testid="ar-auto-tracking-chip"
                data-sync-age={ageSec}
                title={`Observer-local sun/moon re-synced every 60s in AR mode. Last sync ${ageSec}s ago.`}
                style={{
                  background: "rgba(0,0,0,0.65)",
                  color: stale ? "#ff9f43" : "#7ddcff",
                  border: `1px solid ${stale ? "#ff9f43" : "#7ddcff"}`,
                  padding: "5px 10px",
                  borderRadius: 12,
                  fontSize: "0.72rem",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                📡 Live · {ageSec}s
              </span>
            );
          })()}
          {/* Silent auto-recalibration toggle. Once the user has locked
              calibration against the Sun or Moon, a background interval
              silently re-anchors every 60s to compensate for compass
              drift — but only applies the new offset if it's within 10°
              of the existing one (drift-tolerance guard). Tap to toggle
              the silent loop on/off. */}
          {calibrationReference && calibrationReference.source !== "manual" && (
            <button
              onClick={() => setAutoRecalibrate((v) => !v)}
              data-testid="ar-auto-recalibrate-toggle"
              data-active={autoRecalibrate ? "on" : "off"}
              title={autoRecalibrate
                ? "Silent auto-recalibration is ON. The app gently re-anchors against your locked luminary every 60s (drift-tolerant — won't snap if you look away)."
                : "Silent auto-recalibration is OFF. Tap to re-enable gentle drift correction every 60s."}
              style={{
                background: "rgba(0,0,0,0.65)",
                color: autoRecalibrate ? "#7ddcff" : "#888",
                border: `1px solid ${autoRecalibrate ? "#7ddcff" : "#555"}`,
                padding: "5px 10px",
                borderRadius: 12,
                fontSize: "0.72rem",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              {autoRecalibrate ? "🔄 Auto-Recal: ON" : "⏸ Auto-Recal: OFF"}
            </button>
          )}
        </div>
        {calibrationError && (
          <div
            data-testid="ar-calibrate-error"
            style={{
              position: "absolute",
              bottom: 85,
              left: 20,
              right: 20,
              background: "rgba(120,0,0,0.85)",
              color: "#fff",
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: "0.78rem",
              maxWidth: 420
            }}
          >
            ⚠ {calibrationError}
          </div>
        )}

        {/* Aiming-mode banner — appears between the first tap and the
            LOCK tap. Tells the student exactly what to do so they don't
            calibrate against the road, which was the production bug
            reported 2026-06-01. */}
        {aimingMode && (
          <div
            data-testid="ar-aiming-banner"
            style={{
              position: "absolute",
              top: 110,
              left: "50%",
              transform: "translateX(-50%)",
              background: "linear-gradient(135deg, rgba(255,140,0,0.95) 0%, rgba(255,99,71,0.95) 100%)",
              color: "#000",
              padding: "12px 18px",
              borderRadius: 14,
              fontSize: "0.88rem",
              fontWeight: "bold",
              maxWidth: "calc(100% - 40px)",
              textAlign: "center",
              boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
              border: "2px solid #FFD700",
              zIndex: 30,
            }}
          >
            <div style={{ fontSize: "1.05rem", marginBottom: 4 }}>
              🎯 Aim the crosshair at the {aimingTarget === "moon" ? "MOON" : "SUN"}
            </div>
            <div style={{ fontSize: "0.78rem", fontWeight: "normal" }}>
              Hold steady so the gold ring overlaps the real {aimingTarget === "moon" ? "moon" : "sun"} — then tap the orange LOCK button.
            </div>
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={stopARViewport}
          data-testid="ar-close-btn"
          style={{
            position: "absolute",
            bottom: 30,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(255,0,0,0.8)",
            color: "#fff",
            border: "none",
            padding: "15px 40px",
            borderRadius: "30px",
            fontSize: "1rem",
            fontWeight: "bold",
            cursor: "pointer",
            boxShadow: "0 4px 20px rgba(255,0,0,0.3)"
          }}
        >
          ✕ Close AR View
        </button>
      </div>
    );
  }

  return (
    <div data-testid="ar-sky-map">
      {/* Alert Message */}
      {alertMessage && (
        <div style={{
          background: "#1a3a1a",
          border: "1px solid #90EE90",
          padding: "15px",
          borderRadius: "8px",
          marginBottom: "15px",
          color: "#90EE90",
          textAlign: "center"
        }}>
          {alertMessage}
        </div>
      )}

      {/* View Tabs */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
        <button
          onClick={() => setView("sky")}
          style={{
            flex: 1,
            background: view === "sky" ? "#FFD700" : "#333",
            color: view === "sky" ? "#000" : "#fff",
            padding: "12px",
            borderRadius: "8px"
          }}
        >
          🌌 Sky Map
        </button>
        <button
          onClick={toggleARMode}
          data-testid="ar-toggle-btn"
          style={{
            flex: 1,
            background: "#FF6B6B",
            color: "#fff",
            padding: "12px",
            borderRadius: "8px",
            fontWeight: "bold",
            border: "2px solid #FF4444"
          }}
        >
          📱 AR Viewport
        </button>
        <button
          onClick={() => setView("alerts")}
          style={{
            flex: 1,
            background: view === "alerts" ? "#87CEEB" : "#333",
            color: view === "alerts" ? "#000" : "#fff",
            padding: "12px",
            borderRadius: "8px"
          }}
        >
          🔔 Alerts
        </button>
      </div>

      {view === "sky" && (
        <div className="card">
          <h2 style={{ color: "#FFD700", marginBottom: "15px", display: "flex", alignItems: "center", gap: "10px" }}>
            <span>🌌</span> AR Sky Map Overlay
          </h2>
          
          {/* Sky Canvas */}
          <div style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
            background: "#0a0a1a",
            borderRadius: "12px",
            padding: "20px"
          }}>
            <canvas
              ref={canvasRef}
              width={400}
              height={400}
              style={{
                borderRadius: "50%",
                border: "3px solid #333"
              }}
            />
          </div>

          {/* Celestial Data Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
            {/* Sun Card */}
            <div style={{
              background: "#2a2a1a",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #FFD700"
            }}>
              <div style={{ fontSize: "2rem", marginBottom: "10px" }}>☀️</div>
              <h3 style={{ color: "#FFD700", margin: "0 0 10px 0" }}>SUN</h3>
              <div style={{ color: "#eee" }}>
                <div>Azimuth: <strong>{celestialData?.sun?.azimuth?.toFixed(1)}°</strong></div>
                <div>Altitude: <strong>{celestialData?.sun?.altitude?.toFixed(1)}°</strong>
                  {celestialData?.sun?.altitude < 0 && (
                    <span style={{ color: "#888", fontSize: "0.75rem" }}> (below horizon)</span>
                  )}
                </div>
                <div style={{ color: "#87CEEB", marginTop: "10px" }}>
                  Portal: <strong>{celestialData?.enochian?.portal}</strong>
                </div>
              </div>
            </div>

            {/* Moon Card */}
            <div style={{
              background: "#1a1a2a",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #C0C0C0"
            }}>
              <div style={{ fontSize: "2rem", marginBottom: "10px" }}>🌙</div>
              <h3 style={{ color: "#C0C0C0", margin: "0 0 10px 0" }}>MOON</h3>
              <div style={{ color: "#eee" }}>
                <div>Phase: <strong>{celestialData?.moon?.phase_name}</strong></div>
                <div>Illumination: <strong>{(celestialData?.moon?.illumination * 100)?.toFixed(0)}%</strong></div>
                <div style={{ color: "#87CEEB", marginTop: "10px" }}>
                  Age: <strong>{celestialData?.moon?.age?.toFixed(1)} days</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Portal Info */}
          <div style={{
            marginTop: "20px",
            padding: "20px",
            background: "#1a1a1a",
            borderRadius: "12px",
            textAlign: "center"
          }}>
            <h3 style={{ color: "#FFD700", margin: "0 0 10px 0" }}>
              🚪 Current Portal: Gate {celestialData?.enochian?.current_gate ?? celestialData?.enochian?.portal ?? "—"}
            </h3>
            <p style={{ color: "#aaa", margin: "0" }}>
              {celestialData?.enochian?.jubilee_gate ? (
                <span style={{ color: "#90EE90" }}>🎺 JUBILEE GATE DAY - Quarterly Reset!</span>
              ) : (
                `Day ${celestialData?.enochian?.day_of_year ?? celestialData?.biblical?.date?.days_since_abib ?? "—"} of 364`
              )}
            </p>
          </div>

          {/* Refresh Button */}
          <button
            onClick={loadCelestialData}
            style={{
              width: "100%",
              marginTop: "15px",
              background: "#333",
              padding: "12px"
            }}
          >
            🔄 Refresh Sky Data
          </button>
        </div>
      )}

      {view === "alerts" && (
        <div className="card">
          <h2 style={{ color: "#87CEEB", marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
            <span>🔔</span> Celestial Alerts
          </h2>

          {/* New Moon Alerts */}
          <div style={{
            background: "#1a1a2a",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "15px"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ color: "#FFD700", margin: "0 0 5px 0" }}>🌑 New Moon Alerts</h3>
                <p style={{ color: "#888", margin: 0, fontSize: "0.9rem" }}>
                  Get notified when the moon enters a new cycle
                </p>
              </div>
              <button
                onClick={toggleNewMoonAlerts}
                data-testid="toggle-new-moon-alerts"
                style={{
                  background: newMoonAlerts ? "#90EE90" : "#333",
                  color: newMoonAlerts ? "#000" : "#fff",
                  padding: "10px 20px",
                  borderRadius: "20px",
                  fontWeight: "bold"
                }}
              >
                {newMoonAlerts ? "ON" : "OFF"}
              </button>
            </div>
          </div>

          {/* Weekly Sabbath — continuous seventh-day rest (Genesis 2:2-3) */}
          <div style={{
            background: "#1a1a2a",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "15px"
          }}>
            <h3 style={{ color: "#90EE90", margin: "0 0 10px 0" }}>🕯️ Weekly Sabbath</h3>
            <p style={{ color: "#aaa", fontSize: "0.9rem" }}>
              The continuous seventh-day rest established at Creation
              (Genesis 2:2-3). Sabbath begins at sunset Friday and ends
              at sunset Saturday — every week, without interruption.
            </p>
            <div style={{
              display: "flex",
              gap: "10px",
              marginTop: "15px",
              flexWrap: "wrap"
            }}>
              {[
                { label: "Mon", role: "Work 1" },
                { label: "Tue", role: "Work 2" },
                { label: "Wed", role: "Work 3" },
                { label: "Thu", role: "Work 4" },
                { label: "Fri", role: "Preparation" },
                { label: "Sat", role: "Sabbath" },
                { label: "Sun", role: "Work 6" },
              ].map(({ label, role }) => (
                <div key={label} style={{
                  background: role === "Sabbath" ? "#2a3a1a" : "#333",
                  padding: "10px 15px",
                  borderRadius: "8px",
                  border: `1px solid ${role === "Sabbath" ? "#FFD700" : "#90EE90"}`,
                  textAlign: "center"
                }}>
                  <div style={{ color: role === "Sabbath" ? "#FFD700" : "#90EE90", fontWeight: "bold" }}>{label}</div>
                  <div style={{ color: "#aaa", fontSize: "0.7rem" }}>{role}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Jubilee Gate Alerts */}
          <div style={{
            background: "#2a1a1a",
            padding: "20px",
            borderRadius: "12px"
          }}>
            <h3 style={{ color: "#DDA0DD", margin: "0 0 10px 0" }}>🎺 Jubilee Gate Days</h3>
            <p style={{ color: "#aaa", fontSize: "0.9rem", marginBottom: "15px" }}>
              91-Day Quarterly Reset (Days 91, 182, 273, 364)
            </p>
            <div style={{ 
              display: "flex", 
              gap: "10px", 
              flexWrap: "wrap"
            }}>
              {["Q1 (Day 91)", "Q2 (Day 182)", "Q3 (Day 273)", "Q4 (Day 364)"].map((quarter, i) => (
                <div key={i} style={{
                  background: "#333",
                  padding: "10px 15px",
                  borderRadius: "8px",
                  border: "1px solid #DDA0DD",
                  textAlign: "center"
                }}>
                  <div style={{ color: "#DDA0DD", fontWeight: "bold", fontSize: "0.9rem" }}>{quarter}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ARSkyMap;

// Inject the AR-only keyframes once
if (typeof document !== "undefined" && !document.getElementById("ar-skymap-css")) {
  const s = document.createElement("style");
  s.id = "ar-skymap-css";
  s.textContent = `
    @keyframes ar-pulse {
      0%, 100% { transform: scale(1);    box-shadow: 0 0 14px rgba(255,99,71,0.6); }
      50%      { transform: scale(1.06); box-shadow: 0 0 24px rgba(255,99,71,0.95); }
    }
  `;
  document.head.appendChild(s);
}
